import { uiText } from '@/i18n/interface';

import { supabase } from '@/integrations/supabase/client';
import { TaxReportData, ReportPeriod } from '@/types/taxReport';

export const processTaxReportData = async (reportPeriod: ReportPeriod, dojoId: string): Promise<TaxReportData> => {
  if (!dojoId) throw new Error(uiText("A dojo is required for the tax report."));
  const endExclusive = new Date(`${reportPeriod.endDate}T00:00:00Z`);
  endExclusive.setUTCDate(endExclusive.getUTCDate() + 1);
  // Fetch payment data (income)
  const { data: payments, error: paymentsError } = await supabase
    .from('payment_instructions')
    .select(`
      *,
      profiles!inner(first_name, last_name, email)
    `)
    .eq('dojo_id', dojoId)
    .gte('created_at', reportPeriod.startDate)
    .lt('created_at', endExclusive.toISOString());

  if (paymentsError) throw paymentsError;

  // Fetch expenses data
  const { data: expenses, error: expensesError } = await supabase
    .from('expenses')
    .select('*')
    .eq('dojo_id', dojoId)
    .gte('expense_date', reportPeriod.startDate)
    .lte('expense_date', reportPeriod.endDate);

  if (expensesError) throw expensesError;

  // Helper function to format date as dd.mm.yyyy
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Process data - convert all amounts to decimal for consistent calculations
  const reportData: TaxReportData = {
    totalIncome: 0,
    totalExpenses: 0,
    netIncome: 0,
    incomeByCategory: {},
    expensesByCategory: {},
    transactions: []
  };

  // Process payments (income) - convert from cents to decimal
  payments?.forEach(payment => {
    const amountInDecimal = (payment.amount_paid || 0) / 100;
    reportData.totalIncome += amountInDecimal;

    if (!reportData.incomeByCategory[payment.category_name]) {
      reportData.incomeByCategory[payment.category_name] = 0;
    }
    reportData.incomeByCategory[payment.category_name] += amountInDecimal;

    reportData.transactions.push({
      date: formatDate(payment.created_at),
      description: uiText("Payment from {{value0}} {{value1}}", { value0: payment.profiles.first_name, value1: payment.profiles.last_name }),
      category: payment.category_name,
      amount: amountInDecimal,
      type: 'income'
    });
  });

  // Process expenses - convert from cents to decimal
  expenses?.forEach(expense => {
    const amountInDecimal = (expense.amount || 0) / 100;
    reportData.totalExpenses += amountInDecimal;

    if (!reportData.expensesByCategory[expense.category_name]) {
      reportData.expensesByCategory[expense.category_name] = 0;
    }
    reportData.expensesByCategory[expense.category_name] += amountInDecimal;

    reportData.transactions.push({
      date: formatDate(expense.expense_date),
      description: expense.description || `${expense.category_name} expense`,
      category: expense.category_name,
      amount: amountInDecimal,
      type: 'expense'
    });
  });

  reportData.netIncome = reportData.totalIncome - reportData.totalExpenses;
  return reportData;
};
